import { fromString, toString, type MonetaryValue } from 'ezmoney';

import { type PeriodInterval } from '../../models/periodInterval';
import { transformBudgetTreeOutcome } from '../helpers/budgetTree';
import {
  type BranchOf,
  type NodeOf,
  type BudgetTree,
} from '../models/budgetTree';
import { buildApiPeriod, buildPeriod } from '../models/period';

export type BudgetDimension = 'costCenter' | 'expenseCategory' | 'supplier';

export type BudgetPeriodicity = 'monthly' | 'quarterly' | 'yearly' | 'overall';

type Outcome = {
  amounts: MonetaryValue[];
};

export type BudgetDefinitionTree = BudgetTree<Outcome>;

export type BudgetDefinitionBranch = BranchOf<BudgetDefinitionTree>;

export type BudgetDefinitionNode = NodeOf<BudgetDefinitionTree>;

export type BudgetDefinition = {
  id: string;
  companyId: string;
  name: string;
  currency: string;
  period: PeriodInterval;
  periodicity: BudgetPeriodicity;
  definition: BudgetDefinitionTree;
};

export type ApiBudgetDefinition = Omit<
  BudgetDefinition,
  'period' | 'definition'
> & {
  period: { start: string; end: string };
  definition: ApiDefinitionTree;
};

type ApiOutcome = {
  amounts: string[];
};

export type ApiDefinitionTree = BudgetTree<ApiOutcome>;

export type ApiDefinitionBranch = BranchOf<ApiDefinitionTree>;

export type ApiDefinitionNode = NodeOf<ApiDefinitionTree>;

export const convertApiToBudgetDefinition = (
  apiDefinition: ApiBudgetDefinition,
): BudgetDefinition => ({
  id: apiDefinition.id,
  companyId: apiDefinition.companyId,
  name: apiDefinition.name,
  currency: apiDefinition.currency,
  period: buildPeriod(apiDefinition.period),
  periodicity: apiDefinition.periodicity,
  definition: transformBudgetTreeOutcome(
    apiDefinition.definition,
    (outcome) => ({
      amounts: outcome.amounts.map((amount) => fromString(amount)),
    }),
  ),
});

export const convertBudgetDefinitionTreeToApi = (
  definitionTree: BudgetDefinitionTree,
): ApiDefinitionTree =>
  transformBudgetTreeOutcome(definitionTree, (outcome) => ({
    amounts: outcome.amounts.map((amount) => toString(amount)),
  }));

export const convertBudgetDefinitionToApi = (
  budgetDefinition: BudgetDefinition,
): ApiBudgetDefinition => ({
  id: budgetDefinition.id,
  companyId: budgetDefinition.companyId,
  name: budgetDefinition.name,
  currency: budgetDefinition.currency,
  period: buildApiPeriod(budgetDefinition.period),
  periodicity: budgetDefinition.periodicity,
  definition: convertBudgetDefinitionTreeToApi(budgetDefinition.definition),
});
